import { useState, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { inject, observer } from 'mobx-react';
import Loader from '../util/Loader';
import ConfirmActionModal from '../modals/ConfirmActionModal';
import Toast from '../util/Toast';
import Trash from '../svg/Trash';
import CloudDownloadIcon from '../svg/CloudDownloadIcon';
import generalSettingsStore from '@/stores/generalSettingsStore';
import { formatDateTime } from '@/utils/dateFormat';

const ManageCarts = inject('wooCommerceStore')(observer(({ wooCommerceStore }) => {
  const t = useTranslations('Cart');
  const tg = useTranslations('General');
  const dateFormat = generalSettingsStore.dateFormat;
  const [toast, setToast] = useState(null);
  const [savedCarts, setSavedCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [loadConfirmOpen, setLoadConfirmOpen] = useState(false);
  const [selectedCartId, setSelectedCartId] = useState(null);
  const [processingCartId, setProcessingCartId] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const loadCarts = useCallback(async () => {
    setLoading(true);
    try {
      const carts = await wooCommerceStore.getSavedCarts();
      if (carts) {
        setSavedCarts(carts);
      }
    } catch (error) {
      console.error('Error loading carts:', error);
      showToast(t('failed_to_load_carts'), 'error');
    } finally {
      setLoading(false);
    }
  }, [wooCommerceStore, t]);

  useEffect(() => {
    if (generalSettingsStore.showCartManageMultiple) {
      loadCarts();
    }
  }, [loadCarts]);

  const handleDeleteCart = (cartId) => {
    setSelectedCartId(cartId);
    setDeleteConfirmOpen(true);
  };

  const handleLoadCart = (cartId) => {
    setSelectedCartId(cartId);
    setLoadConfirmOpen(true);
  };

  const confirmDeleteCart = async () => {
    setProcessingCartId(selectedCartId);
    try {
      await wooCommerceStore.deleteCart(selectedCartId);
      setSavedCarts(savedCarts.filter(cart => cart.id !== selectedCartId));
      showToast(t('cart_deleted_successfully'), 'success');
    } catch (error) {
      console.error('Error deleting cart:', error);
      showToast(t('failed_to_delete_cart'), 'error');
    } finally {
      setProcessingCartId(null);
      setDeleteConfirmOpen(false);
    }
  };

  const confirmLoadCart = async () => {
    setProcessingCartId(selectedCartId);
    try {
      await wooCommerceStore.loadCart(selectedCartId);

      showToast(t('cart_loaded_successfully'), 'success');
    } catch (error) {
      console.error('Error loading cart:', error);
      showToast(t('failed_to_load_cart'), 'error');
    } finally {
      setProcessingCartId(null);
      setLoadConfirmOpen(false);
    }
  };

  if (!generalSettingsStore.showCartManageMultiple) {
    return null;
  }

  return (
    <section className="p-3">
      <div className="py-8 px-4 mx-auto max-w-2xl lg:py-0">
        <h2 className="mb-4 text-2xl font-bold text-primary-studio font-secondary">
          {t('saved_carts')}
        </h2>
        <hr className="dotted-spaced my-4" />

        {loading ? (
          <div className="p-3 relative">
            <Loader />
          </div>
        ) : savedCarts.length === 0 ? (
          <p className="text-center py-6 text-gray-500">
            {t('no_saved_carts')}
          </p>
        ) : (
          <div className="overflow-x-auto relative">
            <table className="min-w-full table-auto">
              <thead>
                <tr>
                  <th className="border-b font-medium p-2 pl-6 pt-0 pb-3 text-start">{t('cart_name')}</th>
                  <th className="border-b font-medium p-2 pl-6 pt-0 pb-3 text-start">{t('date_created')}</th>
                  <th className="border-b font-medium p-2 pl-6 pt-0 pb-3 text-start">{t('items')}</th>
                  <th className="border-b font-medium p-2 pl-6 pt-0 pb-3 text-start">{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {savedCarts.map((cart) => (
                  <tr key={cart.id} className="hover:bg-gray-50">
                    <td className="border-t border-gray-100 p-2 pl-6">{cart.name}</td>
                    <td className="border-t border-gray-100 p-2 pl-6">{formatDateTime(cart.created_at, dateFormat)}</td>
                    <td className="border-t border-gray-100 p-2 pl-6">{cart.items?.length || 0}</td>
                    <td className="border-t border-gray-100 p-2 pl-6">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleLoadCart(cart.id)}
                          disabled={processingCartId === cart.id}
                          className={`flex items-center justify-center p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-studio focus:ring-offset-2 ${processingCartId === cart.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={t('load_cart')}
                          aria-label={t('load_cart')}
                        >
                          {processingCartId === cart.id ? (
                            <Loader size="sm" />
                          ) : (
                            <CloudDownloadIcon width={20} height={20} stroke="#4F46E5" size="20px" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteCart(cart.id)}
                          disabled={processingCartId === cart.id}
                          className={`flex items-center justify-center p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${processingCartId === cart.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={t('delete')}
                          aria-label={t('delete')}
                        >
                          {processingCartId === cart.id ? (
                            <Loader size="sm" />
                          ) : (
                            <Trash width={20} height={20} stroke="#EF4444" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmActionModal
          isOpen={deleteConfirmOpen}
          title={t('confirm_delete_cart')}
          message={t('delete_cart_confirmation')}
          confirmButtonText={t('delete')}
          cancelButtonText={tg('cancel')}
          onConfirm={confirmDeleteCart}
          onClose={() => setDeleteConfirmOpen(false)}
          isLoading={processingCartId !== null}
          isDanger
        />

        {/* Load Confirmation Modal */}
        <ConfirmActionModal
          isOpen={loadConfirmOpen}
          title={t('confirm_load_cart')}
          message={t('load_cart_confirmation')}
          confirmButtonText={t('load')}
          cancelButtonText={tg('cancel')}
          onConfirm={confirmLoadCart}
          onClose={() => setLoadConfirmOpen(false)}
          isLoading={processingCartId !== null}
        />

        {/* Toast Notification */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </section>
  );
}));

export default ManageCarts;